/**
 * Per-species base HP for the v0.1 species roster + Onix (Brock's ace).
 *
 * Hardcoded rather than dex-resolved because:
 * - 11 entries, ~80 bytes; negligible footprint
 * - Synchronous (no Promise), so computeMaxHp can run inline at battle setup
 * - We already pin the v0.1 roster in SPECIES_TO_DEX (battle/teamFromParty.ts);
 *   any new species must be added to both tables — typecheck won't catch a
 *   mismatch but the runtime fallback below will throw early
 *
 * Values from Gen-3 base stats (matches @pkmn/dex's gen3 species table).
 * If we ever need full dex base stats, swap to `getBaseStats(species)` via
 * the loadGen3 dex helper — but for now hardcode is simpler.
 */
const BASE_HP: Record<string, number> = {
  bulbasaur: 45,
  charmander: 39,
  squirtle: 44,
  caterpie: 45,
  weedle: 40,
  pidgey: 40,
  rattata: 30,
  spearow: 40,
  pikachu: 35,
  geodude: 40,
  onix: 35, // Brock's ace
  metapod: 50, // bug-catcher canon (substituted to caterpie in v0.1; entry kept for future fidelity bump)
  kakuna: 45, // bug-catcher canon (substituted to weedle in v0.1)
  sandshrew: 50, // Liam's L11 (substituted to geodude in v0.1)
};

/**
 * Gen-3 HP stat formula (EV=0; HP isn't natured in Gen 3):
 *
 *   HP = floor((2 * baseHp + hpIv) * level / 100) + level + 10
 *
 * Stats-integrity (2026-08-02): `hpIv` is the member's ROLLED HP IV —
 * the pre-integrity build hardwired 31 here while the sim also played
 * 31s, which kept the two accidentally consistent. Now the sim computes
 * max HP from the rolled IVs teamFromParty passes, so this MUST receive
 * the same IV or stored hp.max desyncs from sim reality (the equality
 * is pinned by stats-integrity.test.ts against the real sim). No
 * default on purpose: every caller decides its IV explicitly.
 *
 * Cartridge cross-check at IV=31 (matches sim + hp-persistence tests):
 *   Bulbasaur(45,L5)  = floor(121*5/100) + 15 = 6 + 15 = 21
 *   Charmander(39,L5) = floor(109*5/100) + 15 = 5 + 15 = 20
 *
 * Throws on unknown species rather than silently returning a placeholder —
 * a missing entry would propagate as wrong max HP through the whole HP
 * persistence chain (save schema, sim injection, bar rendering).
 */
export function computeMaxHp(
  species: string,
  level: number,
  hpIv: number,
): number {
  const base = BASE_HP[species];
  if (base === undefined) {
    throw new Error(
      `computeMaxHp: unknown species '${species}'. Add to data/baseHp.ts.`,
    );
  }
  return Math.floor(((2 * base + hpIv) * level) / 100) + level + 10;
}

/** Exposed for tests + unusual callers. Prefer computeMaxHp() at runtime. */
export const _BASE_HP_TABLE = BASE_HP;
